import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, RefreshCw, Inbox, PenLine, Plus, Pencil, Trash2, Loader2, ArrowLeft, Mail, GraduationCap, Download, Send, MessageSquare, Star, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import { useAuth } from "@/context/AuthContext";

const STATUSES = [
  { key: "new", label: "New" },
  { key: "in_progress", label: "In Progress" },
  { key: "responded", label: "Responded" },
  { key: "closed", label: "Closed" },
];

const statusStyles = {
  new: "bg-tnn-purple text-white",
  in_progress: "bg-amber-100 text-amber-900 border border-amber-300",
  responded: "bg-tnn-soft text-tnn-purple border border-tnn-purple/30",
  closed: "bg-tnn-grey text-tnn-muted border border-tnn-ink/10",
};

const ARTICLE_TOPICS = ["Nutrition", "Digital Health", "AI & Technology", "Research", "Healthcare Innovation", "Education", "Organisation News"];

const inputCls =
  "w-full bg-tnn-warm border border-tnn-ink/12 rounded-xl px-4 py-3 text-sm text-tnn-ink focus:outline-none focus:border-tnn-purple focus:ring-2 focus:ring-tnn-purple/15 transition-[border-color,box-shadow] duration-200";
const labelCls = "block text-[11px] font-bold tracking-[0.18em] uppercase text-tnn-purple mb-2";

function EnquiriesPanel() {
  const navigate = useNavigate();
  const [data, setData] = useState({ enquiries: [], counts: {} });
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/enquiries", { params: filter ? { status: filter } : {} });
      setData(data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin/login");
      else toast.error("Could not load enquiries.");
    } finally {
      setLoading(false);
    }
  }, [filter, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      toast.success("Status updated.");
      load();
    } catch {
      toast.error("Could not update status.");
    }
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5">
        <div>
          <h1 className="font-display text-3xl font-bold text-tnn-ink">Partnership Enquiries</h1>
          <p className="mt-1.5 text-sm text-tnn-muted">Submitted through the tnnafrica.org contact form.</p>
        </div>
        <button
          onClick={load}
          data-testid="admin-refresh-button"
          className="inline-flex items-center gap-2 bg-white border border-tnn-ink/12 px-4 py-2.5 rounded-full text-[10px] font-bold tracking-[0.18em] uppercase text-tnn-muted hover:border-tnn-purple hover:text-tnn-purple transition-colors duration-200"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="mt-7 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {STATUSES.map((s) => (
          <button
            key={s.key}
            onClick={() => setFilter(filter === s.key ? "" : s.key)}
            data-testid={`admin-filter-${s.key}`}
            className={`text-left rounded-2xl border p-5 transition-colors duration-200 ${
              filter === s.key ? "border-tnn-purple bg-white" : "border-tnn-ink/8 bg-white hover:border-tnn-purple/40"
            }`}
          >
            <p className="font-display text-2xl font-bold text-tnn-ink">{data.counts?.[s.key] ?? 0}</p>
            <p className="mt-1 text-[10px] font-bold tracking-[0.18em] uppercase text-tnn-muted">{s.label}</p>
          </button>
        ))}
      </div>

      <div className="mt-7 bg-white border border-tnn-ink/8 rounded-3xl overflow-hidden" data-testid="admin-enquiries-table">
        {loading ? (
          <p className="p-10 text-xs font-bold tracking-[0.2em] uppercase text-tnn-muted">Loading…</p>
        ) : data.enquiries.length === 0 ? (
          <div className="p-16 flex flex-col items-center text-center">
            <Inbox className="w-8 h-8 text-tnn-purple/40" />
            <p className="mt-4 font-display text-xl font-semibold text-tnn-ink">No enquiries yet</p>
            <p className="mt-1.5 text-sm text-tnn-muted">New submissions from the contact form will appear here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-tnn-ink/8 text-left">
                  {["Name", "Organisation", "Email", "Interest", "Message", "Submitted", "Status"].map((h) => (
                    <th key={h} className="px-5 py-4 text-[10px] font-bold tracking-[0.18em] uppercase text-tnn-muted whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.enquiries.map((e) => (
                  <tr key={e.id} className="border-b border-tnn-ink/5 align-top hover:bg-tnn-warm/70 transition-colors duration-150" data-testid={`enquiry-row-${e.id}`}>
                    <td className="px-5 py-4 font-semibold text-tnn-ink whitespace-nowrap">{e.name}</td>
                    <td className="px-5 py-4 text-tnn-muted whitespace-nowrap">{e.organisation || "-"}</td>
                    <td className="px-5 py-4">
                      <a href={`mailto:${e.email}`} className="text-tnn-purple hover:underline whitespace-nowrap">
                        {e.email}
                      </a>
                    </td>
                    <td className="px-5 py-4 text-tnn-muted whitespace-nowrap">{e.interest}</td>
                    <td className="px-5 py-4 text-tnn-muted max-w-xs">
                      <p className="line-clamp-3">{e.message}</p>
                    </td>
                    <td className="px-5 py-4 text-tnn-muted whitespace-nowrap text-xs">
                      {new Date(e.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                    </td>
                    <td className="px-5 py-4">
                      <select
                        value={e.status}
                        onChange={(ev) => updateStatus(e.id, ev.target.value)}
                        data-testid={`enquiry-status-${e.id}`}
                        className={`text-xs font-semibold px-3 py-2 rounded-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-tnn-purple/20 ${statusStyles[e.status] || ""}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s.key} value={s.key}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

const emptyArticle = { title: "", topic: "Nutrition", excerpt: "", body: "", cover: null, published: false };

function ArticlesPanel() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyArticle);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/articles");
      setArticles(data.articles);
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin/login");
      else toast.error("Could not load articles.");
      setArticles([]);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const startNew = () => {
    setForm(emptyArticle);
    setEditing("new");
  };

  const startEdit = (a) => {
    setForm({ title: a.title, topic: a.topic, excerpt: a.excerpt || "", body: a.body, cover: a.cover || null, published: a.published });
    setEditing(a.id);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setForm((f) => ({ ...f, cover: data.url }));
      toast.success("Cover image uploaded.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onBodyUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setForm((f) => ({ ...f, body: `${f.body.trimEnd()}\n\n${data.url}\n\n` }));
      toast.success("Image added to the end of the article. Cut and paste its line to move it anywhere.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onSave = async (publishNow) => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Title and body are required.");
      return;
    }
    setSaving(true);
    const payload = { ...form, published: publishNow ?? form.published };
    try {
      if (editing === "new") {
        await api.post("/admin/articles", payload);
        toast.success(payload.published ? "Article published." : "Draft saved.");
      } else {
        await api.put(`/admin/articles/${editing}`, payload);
        toast.success("Article updated.");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save the article.");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/articles/${a.id}`);
      toast.success("Article deleted.");
      load();
    } catch {
      toast.error("Could not delete the article.");
    }
  };

  if (editing !== null) {
    return (
      <div data-testid="article-editor" className="bg-white border border-tnn-ink/8 rounded-3xl p-8 lg:p-10">
        <button
          onClick={() => setEditing(null)}
          data-testid="editor-back"
          className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.18em] uppercase text-tnn-muted hover:text-tnn-purple transition-colors duration-200"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> All Articles
        </button>
        <h1 className="mt-5 font-display text-2xl font-bold text-tnn-ink">
          {editing === "new" ? "New Article" : "Edit Article"}
        </h1>
        <div className="mt-7 space-y-5">
          <div>
            <label htmlFor="article-title" className={labelCls}>Title *</label>
            <input
              id="article-title"
              data-testid="article-title-input"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="e.g. What we learned building an AI dietitian"
              className={inputCls}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="article-topic" className={labelCls}>Topic</label>
              <select
                id="article-topic"
                data-testid="article-topic-select"
                value={form.topic}
                onChange={(e) => setForm((f) => ({ ...f, topic: e.target.value }))}
                className={`${inputCls} cursor-pointer`}
              >
                {ARTICLE_TOPICS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Cover Image (shown at the top of the article and on the Insights page)</label>
              <div className="flex items-center gap-3">
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onUpload} className="hidden" data-testid="article-cover-input" />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  data-testid="article-cover-button"
                  className="inline-flex items-center gap-2 bg-tnn-soft text-tnn-purple text-xs font-bold px-4 py-2.5 rounded-full hover:bg-tnn-mist transition-colors duration-200"
                >
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                  {form.cover ? "Change Image" : "Upload Image"}
                </button>
                {form.cover && (
                  <>
                    <img src={form.cover} alt="Cover preview" data-testid="article-cover-preview" className="h-10 w-16 object-cover rounded-lg border border-tnn-ink/10" />
                    <button type="button" onClick={() => setForm((f) => ({ ...f, cover: null }))} data-testid="article-cover-remove" className="text-xs font-semibold text-tnn-muted hover:text-red-700">
                      Remove
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
          <div>
            <label htmlFor="article-excerpt" className={labelCls}>Short Excerpt</label>
            <input
              id="article-excerpt"
              data-testid="article-excerpt-input"
              value={form.excerpt}
              onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))}
              placeholder="One or two sentences shown on the Insights page"
              className={inputCls}
            />
          </div>
          <div>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <label htmlFor="article-body" className={labelCls}>Article *</label>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={onBodyUpload}
                className="hidden"
                id="article-body-image"
                data-testid="article-body-image-input"
              />
              <label
                htmlFor="article-body-image"
                data-testid="article-body-image-button"
                className="inline-flex items-center gap-1.5 bg-tnn-soft text-tnn-purple text-[11px] font-bold px-3.5 py-2 rounded-full hover:bg-tnn-mist transition-colors duration-200 cursor-pointer mb-2"
              >
                <Plus className="w-3.5 h-3.5" /> Insert Image
              </label>
            </div>
            <textarea
              id="article-body"
              data-testid="article-body-input"
              rows={14}
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              placeholder="Write the article here. Leave a blank line between paragraphs."
              className={`${inputCls} resize-y leading-relaxed`}
            />
            <p className="mt-2 text-xs text-tnn-muted">
              Tips: a blank line starts a new paragraph, a line starting with "## " becomes a heading, and Insert Image
              adds an image at the end, ready to cut and paste anywhere in the text.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button
              onClick={() => onSave(true)}
              disabled={saving}
              data-testid="article-publish-button"
              className="inline-flex items-center gap-2 bg-tnn-purple text-white font-semibold text-sm px-6 py-3 rounded-full hover:bg-tnn-deep transition-colors duration-200 disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {form.published ? "Save Changes" : "Publish"}
            </button>
            {!form.published && (
              <button
                onClick={() => onSave(false)}
                disabled={saving}
                data-testid="article-draft-button"
                className="inline-flex items-center gap-2 bg-white border border-tnn-ink/15 text-tnn-ink font-semibold text-sm px-6 py-3 rounded-full hover:border-tnn-purple hover:text-tnn-purple transition-colors duration-200 disabled:opacity-60"
              >
                Save as Draft
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5">
        <div>
          <h1 className="font-display text-3xl font-bold text-tnn-ink">Insights Articles</h1>
          <p className="mt-1.5 text-sm text-tnn-muted">Write and publish to the Insights page.</p>
        </div>
        <button
          onClick={startNew}
          data-testid="article-new-button"
          className="inline-flex items-center gap-2 bg-tnn-purple text-white px-5 py-2.5 rounded-full text-xs font-bold tracking-wide hover:bg-tnn-deep transition-colors duration-200"
        >
          <Plus className="w-4 h-4" /> New Article
        </button>
      </div>

      <div className="mt-7 bg-white border border-tnn-ink/8 rounded-3xl overflow-hidden" data-testid="admin-articles-list">
        {articles === null ? (
          <p className="p-10 text-xs font-bold tracking-[0.2em] uppercase text-tnn-muted">Loading…</p>
        ) : articles.length === 0 ? (
          <div className="p-16 flex flex-col items-center text-center">
            <PenLine className="w-8 h-8 text-tnn-purple/40" />
            <p className="mt-4 font-display text-xl font-semibold text-tnn-ink">No articles yet</p>
            <p className="mt-1.5 text-sm text-tnn-muted">Write your first insight and publish it to the site.</p>
          </div>
        ) : (
          <ul className="divide-y divide-tnn-ink/6">
            {articles.map((a) => (
              <li key={a.id} className="flex flex-col sm:flex-row sm:items-center gap-3 px-6 py-5" data-testid={`article-row-${a.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-display font-semibold text-tnn-ink truncate">{a.title}</p>
                    <span
                      className={`text-[10px] font-bold tracking-[0.14em] uppercase px-2.5 py-1 rounded-full ${
                        a.published ? "bg-tnn-purple text-white" : "bg-tnn-grey text-tnn-muted border border-tnn-ink/10"
                      }`}
                      data-testid={`article-status-${a.id}`}
                    >
                      {a.published ? "Published" : "Draft"}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-tnn-muted">
                    {a.topic} · Updated {new Date(a.updated_at).toLocaleDateString("en-GB", { dateStyle: "medium" })}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => startEdit(a)}
                    data-testid={`article-edit-${a.id}`}
                    className="inline-flex items-center gap-1.5 bg-tnn-soft text-tnn-purple text-xs font-bold px-4 py-2 rounded-full hover:bg-tnn-mist transition-colors duration-200"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button
                    onClick={() => onDelete(a)}
                    data-testid={`article-delete-${a.id}`}
                    className="inline-flex items-center gap-1.5 bg-white border border-tnn-ink/12 text-tnn-muted text-xs font-bold px-4 py-2 rounded-full hover:border-red-400 hover:text-red-700 transition-colors duration-200"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

function SubscribersPanel() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get("/admin/subscribers")
      .then((r) => setData(r.data))
      .catch((err) => {
        if (err.response?.status === 401) navigate("/admin/login");
        else {
          toast.error("Could not load subscribers.");
          setData({ subscribers: [], count: 0 });
        }
      });
  }, [navigate]);

  return (
    <>
      <div>
        <h1 className="font-display text-3xl font-bold text-tnn-ink">Newsletter Subscribers</h1>
        <p className="mt-1.5 text-sm text-tnn-muted">
          Readers who signed up from Insights articles. {data ? `${data.count} on the list.` : ""}
        </p>
      </div>
      <div className="mt-7 bg-white border border-tnn-ink/8 rounded-3xl overflow-hidden" data-testid="admin-subscribers-list">
        {data === null ? (
          <p className="p-10 text-xs font-bold tracking-[0.2em] uppercase text-tnn-muted">Loading…</p>
        ) : data.subscribers.length === 0 ? (
          <div className="p-16 flex flex-col items-center text-center">
            <Mail className="w-8 h-8 text-tnn-purple/40" />
            <p className="mt-4 font-display text-xl font-semibold text-tnn-ink">No subscribers yet</p>
            <p className="mt-1.5 text-sm text-tnn-muted">Signups from article pages will appear here.</p>
          </div>
        ) : (
          <ul className="divide-y divide-tnn-ink/6">
            {data.subscribers.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-4 px-6 py-4" data-testid={`subscriber-row-${s.id}`}>
                <a href={`mailto:${s.email}`} className="text-sm font-semibold text-tnn-purple hover:underline">
                  {s.email}
                </a>
                <span className="text-xs text-tnn-muted whitespace-nowrap">
                  {new Date(s.created_at).toLocaleDateString("en-GB", { dateStyle: "medium" })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

const LEAD_STATUSES = [
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "joined", label: "Joined" },
];

const leadStatusStyles = {
  new: "bg-tnn-purple text-white",
  contacted: "bg-amber-100 text-amber-900 border border-amber-300",
  joined: "bg-tnn-soft text-tnn-purple border border-tnn-purple/30",
};

function LeadsPanel() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/masterclass/leads");
      setData(data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin/login");
      else {
        toast.error("Could not load masterclass leads.");
        setData({ leads: [], count: 0 });
      }
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/masterclass/leads/${id}`, { status });
      toast.success("Status updated.");
      load();
    } catch {
      toast.error("Could not update status.");
    }
  };

  const deleteLead = async (l) => {
    if (!window.confirm(`Remove ${l.name} (${l.email}) from the leads list? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/masterclass/leads/${l.id}`);
      toast.success("Lead removed.");
      load();
    } catch {
      toast.error("Could not remove the lead.");
    }
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5">
        <div>
          <h1 className="font-display text-3xl font-bold text-tnn-ink">Masterclass Leads</h1>
          <p className="mt-1.5 text-sm text-tnn-muted">
            Attendees who entered their details before payment. {data ? `${data.count} captured.` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2.5">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/masterclass/leads.csv`}
            data-testid="leads-export-csv"
            className="inline-flex items-center gap-2 bg-tnn-purple text-white px-4 py-2.5 rounded-full text-[10px] font-bold tracking-[0.18em] uppercase hover:bg-tnn-deep transition-colors duration-200"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </a>
          <button
            onClick={load}
            data-testid="leads-refresh-button"
            className="inline-flex items-center gap-2 bg-white border border-tnn-ink/12 px-4 py-2.5 rounded-full text-[10px] font-bold tracking-[0.18em] uppercase text-tnn-muted hover:border-tnn-purple hover:text-tnn-purple transition-colors duration-200"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>
      <div className="mt-7 bg-white border border-tnn-ink/8 rounded-3xl overflow-hidden" data-testid="admin-leads-list">
        {data === null ? (
          <p className="p-10 text-xs font-bold tracking-[0.2em] uppercase text-tnn-muted">Loading…</p>
        ) : data.leads.length === 0 ? (
          <div className="p-16 flex flex-col items-center text-center">
            <GraduationCap className="w-8 h-8 text-tnn-purple/40" />
            <p className="mt-4 font-display text-xl font-semibold text-tnn-ink">No leads yet</p>
            <p className="mt-1.5 text-sm text-tnn-muted">Attendees who start registration will appear here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-tnn-ink/8 text-left">
                  {["Name", "Email", "Payment", "Follow-Up", "Captured", ""].map((h, i) => (
                    <th key={i} className="px-5 py-4 text-[10px] font-bold tracking-[0.18em] uppercase text-tnn-muted whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.leads.map((l) => (
                  <tr key={l.id} className="border-b border-tnn-ink/5 hover:bg-tnn-warm/70 transition-colors duration-150" data-testid={`lead-row-${l.id}`}>
                    <td className="px-5 py-4 font-semibold text-tnn-ink whitespace-nowrap">{l.name}</td>
                    <td className="px-5 py-4">
                      <a href={`mailto:${l.email}`} className="text-tnn-purple hover:underline whitespace-nowrap">
                        {l.email}
                      </a>
                    </td>
                    <td className="px-5 py-4">
                      <span
                        data-testid={`lead-paid-${l.id}`}
                        className={`text-[10px] font-bold tracking-[0.14em] uppercase px-2.5 py-1 rounded-full ${
                          l.paid ? "bg-green-100 text-green-800 border border-green-300" : "bg-tnn-grey text-tnn-muted border border-tnn-ink/10"
                        }`}
                      >
                        {l.paid ? "Paid" : "Unpaid"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <select
                        value={l.status || "new"}
                        onChange={(ev) => updateStatus(l.id, ev.target.value)}
                        data-testid={`lead-status-${l.id}`}
                        className={`text-xs font-semibold px-3 py-2 rounded-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-tnn-purple/20 ${leadStatusStyles[l.status || "new"] || ""}`}
                      >
                        {LEAD_STATUSES.map((s) => (
                          <option key={s.key} value={s.key}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-4 text-tnn-muted whitespace-nowrap text-xs">
                      {new Date(l.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                    </td>
                    <td className="px-5 py-4">
                      <button
                        onClick={() => deleteLead(l)}
                        data-testid={`lead-delete-${l.id}`}
                        aria-label={`Remove ${l.name}`}
                        className="p-2 text-tnn-muted hover:text-red-700 transition-colors duration-200"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

const AUDIENCES = [
  { key: "leads_paid", label: "Paid attendees", hint: "masterclass registrants whose payment is confirmed" },
  { key: "leads_unpaid", label: "Unpaid attendees", hint: "registered but have not completed payment" },
  { key: "leads_all", label: "All masterclass registrants", hint: "everyone who entered their details" },
  { key: "subscribers", label: "Newsletter subscribers", hint: "everyone on the Insights mailing list" },
  { key: "book_waitlist", label: "Book waitlist", hint: "everyone who joined the book waitlist (excluding unsubscribed)" },
  { key: "book_preorders", label: "Book preorders", hint: "readers whose paperback payment is confirmed" },
  { key: "book_readers", label: "Sample chapter readers", hint: "people who downloaded the first chapter" },
];

function EmailsPanel() {
  const navigate = useNavigate();
  const [counts, setCounts] = useState(null);
  const [blasts, setBlasts] = useState(null);
  const [form, setForm] = useState({ audience: "leads_paid", subject: "", message: "", button_text: "", button_url: "", schedule_at: "" });
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([api.get("/admin/email-audiences"), api.get("/admin/email-blasts")]);
      setCounts(a.data);
      setBlasts(b.data.blasts);
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin/login");
      else toast.error("Could not load email tools.");
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const cancelBlast = async (id) => {
    try {
      await api.post(`/admin/email-blasts/${id}/cancel`);
      toast.success("Scheduled email cancelled.");
      load();
    } catch {
      toast.error("Could not cancel (it may have already sent).");
    }
  };

  const send = async (testOnly) => {
    if (!form.subject.trim() || !form.message.trim()) {
      toast.error("Subject and message are required.");
      return;
    }
    const scheduling = Boolean(form.schedule_at) && !testOnly;
    if (!testOnly && !scheduling) {
      const n = counts?.[form.audience] ?? 0;
      if (!window.confirm(`Send "${form.subject}" to ${n} ${n === 1 ? "person" : "people"}? This cannot be undone.`)) return;
    }
    setSending(true);
    try {
      const payload = {
        ...form,
        button_text: form.button_text.trim() || null,
        button_url: form.button_url.trim() || null,
        schedule_at: form.schedule_at ? new Date(form.schedule_at).toISOString() : null,
        test_only: testOnly,
      };
      await api.post("/admin/email-blasts", payload);
      toast.success(
        testOnly
          ? "Test email sent."
          : scheduling
            ? `Scheduled for ${new Date(form.schedule_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}. Recipients are counted when it sends.`
            : "Email is being sent to everyone in that audience."
      );
      setForm((f) => ({ ...f, subject: "", message: "", button_text: "", button_url: "", schedule_at: "" }));
      setTimeout(load, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div>
        <h1 className="font-display text-3xl font-bold text-tnn-ink">Email Studio</h1>
        <p className="mt-1.5 text-sm text-tnn-muted">
          Send the joining link, training materials, feedback forms or newsletters to your audiences.
        </p>
      </div>

      <div className="mt-7 bg-white border border-tnn-ink/8 rounded-3xl p-8 lg:p-10" data-testid="email-composer">
        <div className="space-y-5">
          <div>
            <label htmlFor="blast-audience" className={labelCls}>Audience</label>
            <select
              id="blast-audience"
              data-testid="blast-audience-select"
              value={form.audience}
              onChange={(e) => setForm((f) => ({ ...f, audience: e.target.value }))}
              className={`${inputCls} cursor-pointer`}
            >
              {AUDIENCES.map((a) => (
                <option key={a.key} value={a.key}>
                  {a.label} ({counts ? counts[a.key] ?? 0 : "…"})
                </option>
              ))}
            </select>
            <p className="mt-1.5 text-xs text-tnn-muted">{AUDIENCES.find((a) => a.key === form.audience)?.hint}</p>
          </div>
          <div>
            <label htmlFor="blast-subject" className={labelCls}>Subject *</label>
            <input
              id="blast-subject"
              data-testid="blast-subject-input"
              value={form.subject}
              onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
              placeholder="e.g. Your joining link for Saturday"
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="blast-message" className={labelCls}>Message *</label>
            <textarea
              id="blast-message"
              data-testid="blast-message-input"
              rows={7}
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              placeholder="Write your message here. Leave a blank line between paragraphs."
              className={`${inputCls} resize-y leading-relaxed`}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="blast-button-text" className={labelCls}>Button Text (optional)</label>
              <input
                id="blast-button-text"
                data-testid="blast-button-text-input"
                value={form.button_text}
                onChange={(e) => setForm((f) => ({ ...f, button_text: e.target.value }))}
                placeholder="e.g. Join the Session"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="blast-button-url" className={labelCls}>Button Link (optional, full https address)</label>
              <input
                id="blast-button-url"
                data-testid="blast-button-url-input"
                value={form.button_url}
                onChange={(e) => setForm((f) => ({ ...f, button_url: e.target.value }))}
                placeholder="https://zoom.us/j/..."
                className={inputCls}
              />
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, button_text: "Share Your Feedback", button_url: "https://tnnafrica.org/feedback" }))}
                data-testid="blast-feedback-link-chip"
                className="mt-2 inline-flex items-center gap-1.5 bg-tnn-soft text-tnn-purple text-[11px] font-bold px-3.5 py-2 rounded-full hover:bg-tnn-mist transition-colors duration-200"
              >
                <MessageSquare className="w-3.5 h-3.5" /> Use the feedback form link
              </button>
            </div>
          </div>
          <div>
            <label htmlFor="blast-schedule" className={labelCls}>Schedule For Later (optional, your local time)</label>
            <input
              id="blast-schedule"
              type="datetime-local"
              data-testid="blast-schedule-input"
              value={form.schedule_at}
              onChange={(e) => setForm((f) => ({ ...f, schedule_at: e.target.value }))}
              className={inputCls}
            />
            <p className="mt-1.5 text-xs text-tnn-muted">
              Perfect for the joining link: write it today, schedule it for a few days before the masterclass.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button
              onClick={() => send(false)}
              disabled={sending}
              data-testid="blast-send-button"
              className="inline-flex items-center gap-2 bg-tnn-purple text-white font-semibold text-sm px-6 py-3 rounded-full hover:bg-tnn-deep transition-colors duration-200 disabled:opacity-60"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {form.schedule_at
                ? `Schedule for ${new Date(form.schedule_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}`
                : `Send to ${counts ? counts[form.audience] ?? 0 : "…"} ${counts?.[form.audience] === 1 ? "person" : "people"}`}
            </button>
            <button
              onClick={() => send(true)}
              disabled={sending}
              data-testid="blast-test-button"
              className="inline-flex items-center gap-2 bg-white border border-tnn-ink/15 text-tnn-ink font-semibold text-sm px-6 py-3 rounded-full hover:border-tnn-purple hover:text-tnn-purple transition-colors duration-200 disabled:opacity-60"
            >
              Send test to {counts?.test_recipient || "yourself"}
            </button>
          </div>
        </div>
      </div>

      <div className="mt-7 bg-white border border-tnn-ink/8 rounded-3xl overflow-hidden" data-testid="blast-history">
        <p className="px-6 pt-5 pb-3 text-[10px] font-bold tracking-[0.18em] uppercase text-tnn-muted">Sent History</p>
        {blasts === null ? (
          <p className="p-10 text-xs font-bold tracking-[0.2em] uppercase text-tnn-muted">Loading…</p>
        ) : blasts.length === 0 ? (
          <p className="px-6 pb-8 text-sm text-tnn-muted">Nothing sent yet.</p>
        ) : (
          <ul className="divide-y divide-tnn-ink/6">
            {blasts.map((b) => (
              <li key={b.id} className="flex flex-col sm:flex-row sm:items-center gap-2 px-6 py-4" data-testid={`blast-row-${b.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-tnn-ink truncate">{b.subject}</p>
                  <p className="mt-0.5 text-xs text-tnn-muted">
                    {AUDIENCES.find((a) => a.key === b.audience)?.label || "Test send"} ·{" "}
                    {b.status === "scheduled"
                      ? `scheduled for ${new Date(b.scheduled_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}`
                      : b.status === "cancelled"
                        ? "cancelled"
                        : b.sent === null
                          ? `sending to ${b.recipient_count}`
                          : `${b.sent} of ${b.recipient_count} delivered`}{" "}
                    · {new Date(b.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                  </p>
                </div>
                {b.status === "scheduled" && (
                  <button
                    onClick={() => cancelBlast(b.id)}
                    data-testid={`blast-cancel-${b.id}`}
                    className="shrink-0 inline-flex items-center gap-1.5 bg-white border border-tnn-ink/12 text-tnn-muted text-xs font-bold px-4 py-2 rounded-full hover:border-red-400 hover:text-red-700 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

function FeedbackPanel() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/feedback");
      setData(data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin/login");
      else {
        toast.error("Could not load feedback.");
        setData({ feedback: [], count: 0, avg_rating: null, recommend_pct: null });
      }
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5">
        <div>
          <h1 className="font-display text-3xl font-bold text-tnn-ink">Masterclass Feedback</h1>
          <p className="mt-1.5 text-sm text-tnn-muted">
            Responses from the feedback form at tnnafrica.org/feedback.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="feedback-refresh-button"
          className="inline-flex items-center gap-2 bg-white border border-tnn-ink/12 px-4 py-2.5 rounded-full text-[10px] font-bold tracking-[0.18em] uppercase text-tnn-muted hover:border-tnn-purple hover:text-tnn-purple transition-colors duration-200"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {data && data.count > 0 && (
        <div className="mt-7 grid grid-cols-3 gap-4">
          {[
            { label: "Responses", value: data.count },
            { label: "Average Rating", value: `${data.avg_rating} / 5` },
            { label: "Would Recommend", value: `${data.recommend_pct}%` },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-tnn-ink/8 rounded-2xl p-5" data-testid={`feedback-stat-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <p className="font-display text-2xl font-bold text-tnn-ink">{s.value}</p>
              <p className="mt-1 text-[10px] font-bold tracking-[0.18em] uppercase text-tnn-muted">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-7 bg-white border border-tnn-ink/8 rounded-3xl overflow-hidden" data-testid="admin-feedback-list">
        {data === null ? (
          <p className="p-10 text-xs font-bold tracking-[0.2em] uppercase text-tnn-muted">Loading…</p>
        ) : data.feedback.length === 0 ? (
          <div className="p-16 flex flex-col items-center text-center">
            <MessageSquare className="w-8 h-8 text-tnn-purple/40" />
            <p className="mt-4 font-display text-xl font-semibold text-tnn-ink">No feedback yet</p>
            <p className="mt-1.5 text-sm text-tnn-muted">Send the feedback form link from Email Studio after the masterclass.</p>
          </div>
        ) : (
          <ul className="divide-y divide-tnn-ink/6">
            {data.feedback.map((f) => (
              <li key={f.id} className="px-6 py-5" data-testid={`feedback-row-${f.id}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="flex gap-0.5" data-testid={`feedback-rating-${f.id}`}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-4 h-4 ${f.rating >= n ? "text-tnn-purple fill-tnn-purple" : "text-tnn-ink/15"}`} />
                    ))}
                  </span>
                  <span
                    className={`text-[10px] font-bold tracking-[0.14em] uppercase px-2.5 py-1 rounded-full ${
                      f.recommend === "yes"
                        ? "bg-green-100 text-green-800 border border-green-300"
                        : f.recommend === "maybe"
                          ? "bg-amber-100 text-amber-900 border border-amber-300"
                          : "bg-tnn-grey text-tnn-muted border border-tnn-ink/10"
                    }`}
                  >
                    {f.recommend === "yes" ? "Recommends" : f.recommend === "maybe" ? "Maybe" : "Would not recommend"}
                  </span>
                  <span className="text-xs text-tnn-muted">
                    {f.name || "Anonymous"}
                    {f.email ? ` · ${f.email}` : ""} · {new Date(f.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                  </span>
                </div>
                <p className="mt-2.5 text-sm text-tnn-ink leading-relaxed">
                  <span className="font-semibold text-tnn-purple">Most valuable: </span>{f.most_valuable}
                </p>
                {f.improve && (
                  <p className="mt-1.5 text-sm text-tnn-muted leading-relaxed">
                    <span className="font-semibold">Improve: </span>{f.improve}
                  </p>
                )}
                {f.testimonial && (
                  <p className="mt-1.5 text-sm text-tnn-muted italic leading-relaxed">
                    "{f.testimonial}"{f.testimonial_consent ? " — can quote" : " — private"}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

const WAITLIST_STATUSES = [
  { key: "waitlisted", label: "Waitlisted" },
  { key: "contacted", label: "Contacted" },
  { key: "converted", label: "Converted" },
  { key: "unsubscribed", label: "Unsubscribed" },
];

const waitlistStatusStyles = {
  waitlisted: "bg-tnn-purple text-white",
  contacted: "bg-amber-100 text-amber-900 border border-amber-300",
  converted: "bg-green-100 text-green-800 border border-green-300",
  unsubscribed: "bg-tnn-grey text-tnn-muted border border-tnn-ink/10",
};

function BookPanel() {
  const navigate = useNavigate();
  const [waitlist, setWaitlist] = useState(null);
  const [preorders, setPreorders] = useState(null);
  const [readers, setReaders] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [w, p, r] = await Promise.all([
        api.get("/admin/book/waitlist"),
        api.get("/admin/book/preorders"),
        api.get("/admin/book/readers"),
      ]);
      setWaitlist(w.data.entries);
      setPreorders(p.data.preorders);
      setReaders(r.data.readers);
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin/login");
      else {
        toast.error("Could not load book data.");
        setWaitlist([]);
        setPreorders([]);
        setReaders([]);
      }
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const uploadPdf = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/book/sample-chapter", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Sample chapter PDF replaced.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/book/waitlist/${id}`, { status });
      toast.success("Status updated.");
      load();
    } catch {
      toast.error("Could not update status.");
    }
  };

  const q = query.trim().toLowerCase();
  const filtered = (waitlist || []).filter(
    (e) =>
      (statusFilter === "all" || (e.status || "waitlisted") === statusFilter) &&
      (!q || e.name.toLowerCase().includes(q) || e.email.toLowerCase().includes(q) || e.phone.includes(q))
  );

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5">
        <div>
          <h1 className="font-display text-3xl font-bold text-tnn-ink">Book</h1>
          <p className="mt-1.5 text-sm text-tnn-muted">
            Paperback preorders and waitlist for Building Digital Health Products That Actually Work.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="book-refresh-button"
          className="inline-flex items-center gap-2 bg-white border border-tnn-ink/12 px-4 py-2.5 rounded-full text-[10px] font-bold tracking-[0.18em] uppercase text-tnn-muted hover:border-tnn-purple hover:text-tnn-purple transition-colors duration-200"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Preorders */}
      <div className="mt-7 bg-white border border-tnn-ink/8 rounded-3xl overflow-hidden" data-testid="admin-preorders">
        <p className="px-6 pt-5 pb-3 text-[10px] font-bold tracking-[0.18em] uppercase text-tnn-muted">
          Paperback Preorders {preorders ? `(${preorders.length})` : ""}
        </p>
        {preorders === null ? (
          <p className="p-10 text-xs font-bold tracking-[0.2em] uppercase text-tnn-muted">Loading…</p>
        ) : preorders.length === 0 ? (
          <p className="px-6 pb-8 text-sm text-tnn-muted">
            No preorders yet. Paid preorders appear here automatically after Stripe checkout completes.
          </p>
        ) : (
          <ul className="divide-y divide-tnn-ink/6">
            {preorders.map((o) => (
              <li key={o.id} className="flex items-center gap-4 px-6 py-4 flex-wrap" data-testid={`preorder-row-${o.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-tnn-ink truncate">{o.name || o.email}</p>
                  <p className="mt-0.5 text-xs text-tnn-muted">
                    {o.email} · ${o.amount.toFixed(2)} {o.currency.toUpperCase()} ·{" "}
                    {new Date(o.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                  </p>
                </div>
                <span className="text-[10px] font-bold tracking-[0.14em] uppercase px-2.5 py-1 rounded-full bg-green-100 text-green-800 border border-green-300">
                  Paid
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Sample chapter */}
      <div className="mt-7 bg-white border border-tnn-ink/8 rounded-3xl overflow-hidden" data-testid="admin-sample-readers">
        <div className="px-6 pt-5 pb-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="flex-1 text-[10px] font-bold tracking-[0.18em] uppercase text-tnn-muted">
            Sample Chapter Readers {readers ? `(${readers.length})` : ""}
          </p>
          <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => uploadPdf(e.target.files?.[0])} data-testid="sample-pdf-file-input" />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="sample-pdf-replace-button"
            className="inline-flex items-center justify-center gap-2 bg-white border border-tnn-ink/12 px-4 py-2.5 rounded-full text-[10px] font-bold tracking-[0.18em] uppercase text-tnn-muted hover:border-tnn-purple hover:text-tnn-purple transition-colors duration-200 disabled:opacity-60"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BookOpen className="w-3.5 h-3.5" />}
            Replace Sample PDF
          </button>
        </div>
        {readers === null ? (
          <p className="p-10 text-xs font-bold tracking-[0.2em] uppercase text-tnn-muted">Loading…</p>
        ) : readers.length === 0 ? (
          <p className="px-6 pb-8 text-sm text-tnn-muted">No downloads yet. Readers appear here after entering their email for the sample chapter.</p>
        ) : (
          <ul className="divide-y divide-tnn-ink/6">
            {readers.map((r) => (
              <li key={r.id} className="flex items-center gap-4 px-6 py-3.5" data-testid={`reader-row-${r.id}`}>
                <p className="flex-1 text-sm text-tnn-ink truncate">{r.email}</p>
                <p className="text-xs text-tnn-muted whitespace-nowrap">
                  {new Date(r.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Waitlist */}
      <div className="mt-7 bg-white border border-tnn-ink/8 rounded-3xl overflow-hidden" data-testid="admin-waitlist">
        <div className="px-6 pt-5 pb-4 flex flex-col lg:flex-row lg:items-center gap-3">
          <p className="flex-1 text-[10px] font-bold tracking-[0.18em] uppercase text-tnn-muted">
            Waitlist {waitlist ? `(${filtered.length}${filtered.length !== waitlist.length ? ` of ${waitlist.length}` : ""})` : ""}
          </p>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email or phone"
            data-testid="waitlist-search-input"
            className="bg-white border border-tnn-ink/12 rounded-full px-4 py-2.5 text-sm text-tnn-ink placeholder:text-tnn-muted/60 focus:outline-none focus:border-tnn-purple focus:ring-2 focus:ring-tnn-purple/15 lg:w-64"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="waitlist-filter-select"
            className="bg-white border border-tnn-ink/12 rounded-full px-4 py-2.5 text-sm text-tnn-ink cursor-pointer focus:outline-none focus:border-tnn-purple"
          >
            <option value="all">All statuses</option>
            {WAITLIST_STATUSES.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/book/waitlist.csv`}
            data-testid="waitlist-export-csv"
            className="inline-flex items-center justify-center gap-2 bg-tnn-purple text-white px-4 py-2.5 rounded-full text-[10px] font-bold tracking-[0.18em] uppercase hover:bg-tnn-deep transition-colors duration-200"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </a>
        </div>
        {waitlist === null ? (
          <p className="p-10 text-xs font-bold tracking-[0.2em] uppercase text-tnn-muted">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="px-6 pb-8 text-sm text-tnn-muted">
            {waitlist.length === 0 ? "No one on the waitlist yet." : "No entries match that search or filter."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-tnn-ink/8 text-left">
                  {["Name", "Email", "Phone", "Status", "Joined"].map((h) => (
                    <th key={h} className="px-5 py-3.5 text-[10px] font-bold tracking-[0.18em] uppercase text-tnn-muted whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr key={e.id} className="border-b border-tnn-ink/5 hover:bg-tnn-warm/70 transition-colors duration-150" data-testid={`waitlist-row-${e.id}`}>
                    <td className="px-5 py-4 font-semibold text-tnn-ink whitespace-nowrap">{e.name}</td>
                    <td className="px-5 py-4">
                      <a href={`mailto:${e.email}`} className="text-tnn-purple hover:underline whitespace-nowrap">{e.email}</a>
                    </td>
                    <td className="px-5 py-4 text-tnn-muted whitespace-nowrap">{e.phone}</td>
                    <td className="px-5 py-4">
                      <select
                        value={e.status || "waitlisted"}
                        onChange={(ev) => updateStatus(e.id, ev.target.value)}
                        data-testid={`waitlist-status-${e.id}`}
                        className={`text-xs font-semibold px-3 py-2 rounded-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-tnn-purple/20 ${waitlistStatusStyles[e.status || "waitlisted"] || ""}`}
                      >
                        {WAITLIST_STATUSES.map((s) => (
                          <option key={s.key} value={s.key}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-4 text-tnn-muted whitespace-nowrap text-xs">
                      {new Date(e.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("enquiries");

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-tnn-warm" data-testid="admin-dashboard">
      <header className="bg-white border-b border-tnn-ink/8">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/assets/logo.png" alt="TNN Africa" className="h-8 w-auto" />
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-tnn-muted">Admin</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/" data-testid="admin-view-site" className="text-[10px] font-bold tracking-[0.18em] uppercase text-tnn-muted hover:text-tnn-purple transition-colors duration-200">
              View Site
            </Link>
            <span className="hidden sm:block text-xs text-tnn-muted">{user?.email}</span>
            <button
              onClick={onLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.18em] uppercase text-tnn-muted hover:text-tnn-purple transition-colors duration-200"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 pb-4 flex gap-2">
          {[
            { key: "enquiries", label: "Enquiries", icon: Inbox },
            { key: "articles", label: "Articles", icon: PenLine },
            { key: "subscribers", label: "Subscribers", icon: Mail },
            { key: "leads", label: "Masterclass", icon: GraduationCap },
            { key: "feedback", label: "Feedback", icon: MessageSquare },
            { key: "book", label: "Book", icon: BookOpen },
            { key: "emails", label: "Emails", icon: Send },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`inline-flex items-center gap-2 text-xs font-bold px-5 py-2.5 rounded-full transition-colors duration-200 ${
                tab === t.key ? "bg-tnn-purple text-white" : "bg-tnn-warm text-tnn-muted hover:text-tnn-purple border border-tnn-ink/10"
              }`}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {tab === "enquiries" ? <EnquiriesPanel /> : tab === "articles" ? <ArticlesPanel /> : tab === "subscribers" ? <SubscribersPanel /> : tab === "leads" ? <LeadsPanel /> : tab === "feedback" ? <FeedbackPanel /> : tab === "book" ? <BookPanel /> : <EmailsPanel />}
      </main>
    </div>
  );
}
